/**
 * Recommended listings — which listings may appear under a listing, and
 * in what order.
 *
 * Single source of truth for the recommendation rules. The same rules
 * drive the listing-page recommendation rows today and are meant to drive
 * later features (recently-viewed feeds, proactive emails, personalised
 * feeds).
 *
 * ## The one rule
 *
 * A recommended item must fit the listing the buyer is looking at. Items
 * that cannot physically fit are never shown, and the row is never padded
 * to look full — it shrinks, or it disappears (BR-1, BR-6).
 *
 * ## Hard constraints, per category
 *
 *   Complete wheel, car model known → same car make + model.
 *   Complete wheel, no car model    → same bolt pattern + same dimension.
 *   Tyre                            → same tyre size + same season type.
 *   Rim                             → same bolt pattern + same diameter.
 *   Any listing with a staggered
 *   front/rear pair                 → same front+rear pair (BR-5).
 *
 * Everything else — season on wheels, condition, brand, ET, hub bore — is
 * soft: it moves an item up the row, it never lets an item into the row.
 *
 * ## Why the check runs twice
 *
 * `buildRecommendationQuery` narrows the search server-side, and
 * `isFitMatch` re-checks every candidate that comes back. That looks
 * redundant, and it is not: the marketplace API **silently ignores** a
 * `pub_*` filter on a key that has no search index, returning the whole
 * unfiltered pool with a 200. A query is therefore best-effort narrowing;
 * `isFitMatch` is the guarantee. Never render a candidate that has not
 * been through it.
 *
 * Values are compared through `util/listingFacets`, because the same
 * measurement is stored in more than one spelling ("114_3" / "114.3",
 * "volvo" / "Volvo") and because "other" and "blank" are stored values
 * that mean "unanswered".
 */

import { buildCombination } from './listingCombination';
import { boltCircleSpellings, boltCircleText } from './boltCircleCore';
import {
  isUsableFacetValue,
  normalizeCategoryId,
  normalizeFacetValue,
} from './listingFacets';

// Consumer + business variants share the same compatibility group.
// The trap: "businesswheel" (no dash) is the business RIM type, while
// "business-wheel" (dash) is a complete wheel.
export const LISTING_GROUPS = {
  wheel: ['wheel', 'business-wheel'],
  tire: ['tire', 'businesstire'],
  rim: ['rim', 'businesswheel'],
};

/** Which hard constraint a listing is matched by. */
export const MATCH_KIND = {
  /** Complete wheel tagged with a car make + model. */
  CAR_MODEL: 'carModel',
  /** Rim, or a complete wheel with no car model: bolt pattern + diameter. */
  BOLT_PATTERN: 'boltPattern',
  /** Tyre: the tyre size set. */
  TYRE_SIZE: 'tyreSize',
};

export const MAX_RECOMMENDATIONS = 8;
export const MAX_SELLER_LISTINGS = 8;

// How many candidates to ask the API for. Larger than the row so the soft
// ranking has something to choose between after the hard filter drops the
// non-fitting ones. 100 is the API's ceiling.
export const CANDIDATE_POOL_SIZE = 50;
export const SELLER_POOL_SIZE = 24;

export const getListingGroup = listingType => {
  if (LISTING_GROUPS.wheel.includes(listingType)) return 'wheel';
  if (LISTING_GROUPS.tire.includes(listingType)) return 'tire';
  if (LISTING_GROUPS.rim.includes(listingType)) return 'rim';
  return null;
};

const publicDataOf = listing => listing?.attributes?.publicData || {};

const facetOrNull = value => (isUsableFacetValue(value) ? normalizeFacetValue(value) : null);
const categoryOrNull = value => (isUsableFacetValue(value) ? normalizeCategoryId(value) : null);

/**
 * The tyre sizes a wheel or tyre listing covers, e.g. ["235/55R19"], or
 * ["245/45R19", "275/40R19"] for a staggered set.
 *
 * `listingType` is pinned to 'tire' rather than passed through, because
 * `buildCombination` picks its format from `adType` first and some
 * complete-wheel listings carry a stale `adType: 'rim'` — which would
 * produce rim strings ("19 ET45") where a tyre size is wanted. Rims get
 * an empty list: on a rim, `tireWidthFront` holds the *rim* width and
 * `profileFront` holds unrelated leftovers, so any size built from them
 * would be fiction.
 */
const tyreSizesOf = (publicData, group) => {
  if (group === 'rim') return [];
  return buildCombination({
    listingType: 'tire',
    diameter: publicData.diameter,
    tireWidthFront: publicData.tireWidthFront,
    profileFront: publicData.profileFront,
    tireWidthRear: publicData.tireWidthRear,
    profileRear: publicData.profileRear,
  }).map(size => size.toUpperCase());
};

/**
 * Read every fitment-relevant value off a listing, normalised.
 * Returns null when the listing is not one of the three groups.
 */
export const getFitmentFacets = listing => {
  const publicData = publicDataOf(listing);
  const group = getListingGroup(publicData.listingType);
  if (!group) return null;

  return {
    group,
    make: categoryOrNull(publicData.categoryLevel1),
    model: categoryOrNull(publicData.categoryLevel2),
    diameter: facetOrNull(publicData.diameter),
    bolts: facetOrNull(publicData.numberOfBolts),
    /* "1397" is Console's key for 139.7 (P25009-488); facetOrNull would read
     * it as 1397 and part it from every "139.7" listing. */
    boltCircle: isUsableFacetValue(publicData.boltCircle)
      ? boltCircleText(publicData.boltCircle) || facetOrNull(publicData.boltCircle)
      : null,
    season: facetOrNull(publicData.tiretype),
    sizes: tyreSizesOf(publicData, group),
    // Soft-only facets. ET is NaN rather than 0 when unrecorded — 0 is a
    // real offset, so treating "missing" as 0 would pull every rim with a
    // near-zero ET to the front of a row it has nothing to do with.
    condition: facetOrNull(publicData.condition),
    brand: facetOrNull(publicData.brand),
    et: isUsableFacetValue(publicData.etFront)
      ? Number(normalizeFacetValue(publicData.etFront))
      : NaN,
    hubHole: facetOrNull(publicData.hubHole),
  };
};

/** True when the listing covers two different axle sizes. */
export const isStaggered = facets => (facets?.sizes?.length || 0) > 1;

/**
 * The constraints a candidate must satisfy to be shown at all.
 * Returns null when the listing does not record enough to prove that
 * anything fits it — in that case the row is hidden rather than filled
 * with guesses.
 */
export const getHardConstraints = facets => {
  if (!facets) return null;
  const { group, make, model, bolts, boltCircle, diameter, sizes, season } = facets;

  // A staggered listing may only be matched by the same front+rear pair,
  // whichever category it is in (BR-5). A rear-only item covers one of
  // the two sizes and is therefore not an alternative to the set.
  const pairConstraint = isStaggered(facets) ? sizes : null;
  const hasBoltPattern = !!bolts && !!boltCircle;

  if (group === 'rim') {
    // BR-4: bolt pattern + diameter, nothing relaxed.
    if (!hasBoltPattern || !diameter) return null;
    return { kind: MATCH_KIND.BOLT_PATTERN, bolts, boltCircle, diameter, sizes: null, season: null };
  }

  if (group === 'tire') {
    // BR-3: same size, same season. When the seller never recorded a
    // season we cannot enforce one — size alone still guarantees fit.
    if (!sizes.length) return null;
    return { kind: MATCH_KIND.TYRE_SIZE, sizes, season: season || null };
  }

  // Complete wheels. A car model is the strongest fitment statement we
  // hold, so when the listing has one it is the hard rule and the row
  // never leaves that car (BR-2, AC-1). Make alone is not enough — an
  // XC90 wheel does not fit a V40.
  if (make && model) {
    return { kind: MATCH_KIND.CAR_MODEL, make, model, sizes: pairConstraint, season: null };
  }

  // No car model: fall back to what physically bolts on. The size string
  // carries the diameter, so it replaces the diameter check when present.
  if (hasBoltPattern && (sizes.length || diameter)) {
    return {
      kind: MATCH_KIND.BOLT_PATTERN,
      bolts,
      boltCircle,
      diameter: sizes.length ? null : diameter,
      sizes: sizes.length ? sizes : null,
      season: null,
    };
  }

  return null;
};

const sameSizeSet = (a = [], b = []) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

/**
 * The guarantee behind BR-1: does this candidate actually fit?
 *
 * Runs on every candidate before it is rendered, including candidates the
 * API already claims to have filtered. See the module comment for why.
 */
export const isFitMatch = (hard, group, candidate) => {
  if (!hard) return false;
  const facets = getFitmentFacets(candidate);
  if (!facets || facets.group !== group) return false;

  if (hard.kind === MATCH_KIND.CAR_MODEL) {
    if (facets.make !== hard.make || facets.model !== hard.model) return false;
  }

  if (hard.kind === MATCH_KIND.BOLT_PATTERN) {
    if (facets.bolts !== hard.bolts || facets.boltCircle !== hard.boltCircle) return false;
    if (hard.diameter && facets.diameter !== hard.diameter) return false;
  }

  // Exact set equality, not "contains": a staggered set is not an
  // alternative to a single size, and a single size is not an
  // alternative to a staggered set.
  if (hard.sizes && !sameSizeSet(hard.sizes, facets.sizes)) return false;

  // A candidate with no recorded season cannot be shown as the same
  // season (AC-2 allows no summer tyre onto a winter row, and "unknown"
  // is not a proof of winter).
  if (hard.season && facets.season !== hard.season) return false;

  return true;
};

/* ---------------------------------------------------------------- *
 * Queries
 * ---------------------------------------------------------------- */

/* The same category id appears in several casings across listings
 * ("vw" / "VW" / "Volvo" / "volvo"), and the API matches the stored
 * string exactly. Send the spellings we know of; `isFitMatch` still has
 * the final say. */
const caseVariants = value => {
  const raw = String(value).trim();
  const lower = raw.toLowerCase();
  const titled = lower.replace(/\b[a-zåäö]/g, letter => letter.toUpperCase());
  return [...new Set([raw, lower, titled, raw.toUpperCase()])];
};

/* Same idea for measurements stored as "114.3" in one place and "114_3"
 * in another. Unknown values are ignored by the API, not rejected. */
const specVariants = value => {
  const raw = String(value).trim();
  return [...new Set([raw, raw.replace(/_/g, '.'), raw.replace(/\./g, '_')])];
};

/**
 * Build the `listings.query()` params for the fit row.
 *
 * Only hard constraints go in. Soft preferences are applied by
 * `rankFitMatches` after the fact, so that relaxing them can never
 * require a second round trip and can never widen the row past what
 * fits.
 *
 * Returns null when the listing cannot support a fit row at all.
 */
export const buildRecommendationQuery = (listing, opts = {}) => {
  const facets = getFitmentFacets(listing);
  const hard = getHardConstraints(facets);
  if (!hard) return null;

  const publicData = publicDataOf(listing);
  const { perPage = CANDIDATE_POOL_SIZE } = opts;

  const params = {
    pub_listingType: LISTING_GROUPS[facets.group],
    perPage,
    page: 1,
    sort: '-createdAt',
  };

  if (hard.kind === MATCH_KIND.CAR_MODEL) {
    params.pub_categoryLevel1 = caseVariants(publicData.categoryLevel1);
    params.pub_categoryLevel2 = caseVariants(publicData.categoryLevel2);
  }

  if (hard.kind === MATCH_KIND.BOLT_PATTERN) {
    params.pub_numberOfBolts = specVariants(publicData.numberOfBolts);
    // Every stored spelling, `1397` included (P25009-488).
    const circles = boltCircleSpellings(publicData.boltCircle);
    params.pub_boltCircle = circles.length > 0 ? circles : specVariants(publicData.boltCircle);
    if (hard.diameter) {
      params.pub_diameter = specVariants(publicData.diameter);
    }
  }

  // `has_all` is what makes a staggered set match a staggered set: the
  // candidate must carry both axle sizes, not just one of them.
  if (hard.sizes) {
    params.pub_combination = `has_all:${hard.sizes.join(',')}`;
  }

  if (hard.season) {
    params.pub_tiretype = publicData.tiretype;
  }

  return params;
};

/** Params for the "more from this seller" row. No fitment filter (BR-7). */
export const buildSellerListingsQuery = (authorId, opts = {}) => {
  if (!authorId) return null;
  const { perPage = SELLER_POOL_SIZE } = opts;
  return { authorId, perPage, page: 1, sort: '-createdAt' };
};

/** Author id from either a raw API entity or a denormalised one. */
export const getAuthorId = listing =>
  listing?.author?.id?.uuid || listing?.relationships?.author?.data?.id?.uuid || null;

/* ---------------------------------------------------------------- *
 * Ranking
 * ---------------------------------------------------------------- */

/* Soft preferences, best-first. These only reorder a row whose members
 * all already fit, so the weights decide "which fitting item first", not
 * "which item at all". Season leads because a buyer looking at winter
 * wheels in October is not shopping for summer ones. */
const SOFT_WEIGHTS = {
  season: 8,
  sizes: 6,
  diameter: 4,
  boltPattern: 3,
  staggered: 2,
  et: 2,
  hubHole: 1,
  condition: 1,
  brand: 1,
};

const ET_TOLERANCE_MM = 5;

const softScore = (current, candidate) => {
  let score = 0;
  if (current.season && candidate.season === current.season) score += SOFT_WEIGHTS.season;
  if (current.sizes.length && sameSizeSet(current.sizes, candidate.sizes)) {
    score += SOFT_WEIGHTS.sizes;
  }
  if (current.diameter && candidate.diameter === current.diameter) score += SOFT_WEIGHTS.diameter;
  if (
    current.bolts &&
    current.boltCircle &&
    candidate.bolts === current.bolts &&
    candidate.boltCircle === current.boltCircle
  ) {
    score += SOFT_WEIGHTS.boltPattern;
  }
  if (isStaggered(current) === isStaggered(candidate)) score += SOFT_WEIGHTS.staggered;
  if (
    Number.isFinite(current.et) &&
    Number.isFinite(candidate.et) &&
    Math.abs(current.et - candidate.et) <= ET_TOLERANCE_MM
  ) {
    score += SOFT_WEIGHTS.et;
  }
  if (current.hubHole && candidate.hubHole === current.hubHole) score += SOFT_WEIGHTS.hubHole;
  if (current.condition && candidate.condition === current.condition) score += SOFT_WEIGHTS.condition;
  if (current.brand && candidate.brand === current.brand) score += SOFT_WEIGHTS.brand;
  return score;
};

/* Sort without disturbing the incoming order (newest first) inside a
 * score bucket. Array.prototype.sort is not guaranteed stable on every
 * engine we ship to, so the index is part of the comparison. */
const stableSortByScore = scored =>
  scored
    .map((entry, index) => ({ ...entry, index }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(entry => entry.listing);

/**
 * Drop everything that does not fit, then order what is left by how
 * close it is. Never returns more than `MAX_RECOMMENDATIONS`; returning
 * fewer — or none — is the expected outcome on a thin category (BR-6).
 */
export const rankFitMatches = (currentListing, candidates = [], opts = {}) => {
  const facets = getFitmentFacets(currentListing);
  const hard = getHardConstraints(facets);
  if (!hard) return [];

  const { limit = MAX_RECOMMENDATIONS } = opts;
  const currentId = currentListing?.id?.uuid;

  const scored = candidates
    .filter(candidate => candidate?.id?.uuid && candidate.id.uuid !== currentId)
    .filter(candidate => isFitMatch(hard, facets.group, candidate))
    .map(listing => ({ listing, score: softScore(facets, getFitmentFacets(listing)) }));

  return stableSortByScore(scored).slice(0, limit);
};

/**
 * Order the seller's other listings: same category as the one on screen
 * first, then newest (the order the API returned them in).
 */
export const rankSellerListings = (currentListing, candidates = [], opts = {}) => {
  const { limit = MAX_SELLER_LISTINGS } = opts;
  const currentId = currentListing?.id?.uuid;
  const currentGroup = getListingGroup(publicDataOf(currentListing).listingType);

  const scored = candidates
    .filter(candidate => candidate?.id?.uuid && candidate.id.uuid !== currentId)
    .map(listing => ({
      listing,
      score:
        currentGroup && getListingGroup(publicDataOf(listing).listingType) === currentGroup ? 1 : 0,
    }));

  return stableSortByScore(scored).slice(0, limit);
};

export const excludeListingById = (listings, listingId) =>
  listings.filter(listing => listing?.id?.uuid !== listingId);
